/**
 * WelcomeScreen Component
 *
 * Full-screen overlay shown once on first operator login.
 * Displays Guild logo, personalized welcome message, value prop,
 * and a single "Let's go" CTA that dismisses the screen.
 *
 * Persistence: localStorage key 'op_seenWelcome' (upgraded to DB in 21.1.4).
 * Renders on top of OperatorLayout (z-[70], above BottomSheet z-[60] and header z-50).
 */

import { cn } from '../../../lib/utils';
import logo from '../../../assets/images/logo.png';

interface WelcomeScreenProps {
  /** Operator's first name */
  firstName: string;
  /** Called when the operator taps "Let's go" */
  onDismiss: () => void;
}

export function WelcomeScreen({ firstName, onDismiss }: WelcomeScreenProps) {
  return (
    <div
      className="fixed inset-0 z-[70] flex flex-col items-center justify-center bg-op-surface px-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="welcome-heading"
      aria-describedby="welcome-description"
    >
      {/* Content container -- fadeInUp animation */}
      <div
        className="flex flex-col items-center text-center"
        style={{ animation: 'fadeInUp 0.4s ease-out' }}
      >
        {/* Guild logo */}
        <img
          src={logo}
          alt="Guild Insights"
          className="h-12 w-auto"
        />

        {/* Welcome heading */}
        <h1
          id="welcome-heading"
          className="mt-6 text-2xl font-bold tracking-tight text-op-text-primary"
        >
          Welcome, {firstName}!
        </h1>

        {/* Value proposition */}
        <p id="welcome-description" className="mt-3 max-w-[260px] text-sm leading-relaxed text-op-text-secondary">
          Track your progress, complete training, and grow your skills.
        </p>
      </div>

      {/* CTA button -- delayed fadeInUp */}
      <button
        type="button"
        onClick={onDismiss}
        autoFocus
        className={cn(
          'mt-10 min-h-[48px] w-full max-w-[260px] rounded-xl',
          'bg-guild-green px-8 py-3.5 text-base font-semibold text-white',
          'active:bg-guild-green/90 transition-colors duration-150',
          'focus-visible:ring-2 focus-visible:ring-guild-green focus-visible:ring-offset-2',
        )}
        style={{ animation: 'fadeInUp 0.4s ease-out 0.2s both' }}
      >
        Let&apos;s go
      </button>
    </div>
  );
}
